import type { JSX } from 'react';
import { useState } from 'react';
import { PENNYLANE_ORIGIN } from '@/components/filters/origin-filter.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet.js';
import { importInvoiceByNumber } from '@/features/invoices/api.js';
import { useStoresList } from '@/features/stores/use-stores-list.js';
import { ApiError } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import { useImportOrder } from './use-order-actions.js';

export interface ImportOrderSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Chemin de la fiche à ouvrir (commande ou facture) — `01` §4 et `01` §6.4. */
  onImported: (target: string) => void;
}

/**
 * « Importer… » (`02` §3) : récupère une commande Shopify ou une facture
 * Pennylane par son numéro. Un tiroir plutôt qu'une pop-up — règle générale
 * de la refonte (`README-shell.md`).
 */
export function ImportOrderSheet({
  open,
  onOpenChange,
  onImported,
}: ImportOrderSheetProps): JSX.Element {
  const { data: storesPage } = useStoresList();
  const stores = storesPage?.items ?? [];
  const [origin, setOrigin] = useState('');
  const [reference, setReference] = useState('');
  const [confirmResend, setConfirmResend] = useState(false);
  const [pendingInvoice, setPendingInvoice] = useState(false);

  const importOrder = useImportOrder();
  const isPennylane = origin === PENNYLANE_ORIGIN;

  function reset(): void {
    setOrigin('');
    setReference('');
    setPendingInvoice(false);
  }

  function close(): void {
    reset();
    onOpenChange(false);
  }

  async function importInvoice(confirm: boolean): Promise<void> {
    setPendingInvoice(true);
    try {
      const result = await importInvoiceByNumber(reference.trim(), confirm);
      toast.success(`Facture ${result.invoiceNumber} importée.`);
      close();
      setConfirmResend(false);
      onImported(`/commandes/factures/${result.id}`);
    } catch (error: unknown) {
      // 409 : la facture a déjà été transmise — `01` §6.4 impose une
      // confirmation explicite avant de la renvoyer.
      if (error instanceof ApiError && error.status === 409) {
        setConfirmResend(true);
      } else {
        toast.fromError(error);
      }
    } finally {
      setPendingInvoice(false);
    }
  }

  const canSubmit = origin !== '' && reference.trim() !== '';
  const isPending = importOrder.isPending || pendingInvoice;

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            reset();
          }
          onOpenChange(next);
        }}
      >
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Importer</SheetTitle>
            <SheetDescription>
              Récupère une commande Shopify ou une facture Pennylane par son numéro. Si elle est
              inconnue, elle est créée et préparée immédiatement ; si elle est déjà connue, sa fiche
              s’ouvre directement.
            </SheetDescription>
          </SheetHeader>

          <form
            className="flex flex-1 flex-col gap-4"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSubmit) {
                return;
              }
              if (isPennylane) {
                void importInvoice(false);
                return;
              }
              importOrder.mutate(
                { store: origin, reference: reference.trim() },
                {
                  onSuccess: (result) => {
                    toast.success(
                      result.created
                        ? `Commande ${result.orderId} importée.`
                        : 'Commande déjà connue — ouverture de sa fiche.',
                    );
                    close();
                    onImported(`/commandes/${result.id}`);
                  },
                },
              );
            }}
          >
            <Field label="Origine" htmlFor="import-origin">
              <Select value={origin} onValueChange={setOrigin}>
                <SelectTrigger id="import-origin" aria-label="Origine">
                  <SelectValue placeholder="Choisir une origine" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map((item) => (
                    <SelectItem key={item.code} value={item.code}>
                      {item.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={PENNYLANE_ORIGIN}>Pennylane</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field
              label={
                isPennylane
                  ? 'Numéro ou identifiant de facture'
                  : 'Numéro ou identifiant de commande'
              }
              htmlFor="import-reference"
            >
              <Input
                id="import-reference"
                value={reference}
                onChange={(event) => {
                  setReference(event.target.value);
                }}
                placeholder={isPennylane ? 'FA-2026-0142' : '#391146'}
              />
            </Field>

            <SheetFooter>
              <Button type="button" variant="outline" onClick={close} disabled={isPending}>
                Annuler
              </Button>
              <Button type="submit" isLoading={isPending} disabled={!canSubmit}>
                Importer
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmResend}
        onOpenChange={setConfirmResend}
        title="Facture déjà transmise"
        description={`La facture ${reference.trim()} a déjà été transmise à Martelet. La renvoyer peut créer un doublon si le premier envoi a été intégré.`}
        confirmLabel="Renvoyer quand même"
        isConfirming={pendingInvoice}
        onConfirm={() => {
          void importInvoice(true);
        }}
      />
    </>
  );
}
